import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Modal } from 'reactstrap'
import GlobalContext from '../../providers/GlobalContext'
import useUnify from '../../hooks/useUnify'
import { updateUnifyPreference } from '../../helpers/session'
import { INTRO_OPEN_CLASS } from './bannerSchedule'
import { TOUR } from './copy'

const imageUrl = (name) => `${process.env.PUBLIC_URL || ''}/img/unify/tour/${name}.webp`

// The "what's new in Unify" tour for existing Sematics users: one slide per
// key change, then a last step to switch, take a look, or carry on in classic.
const UnifyIntroModal = React.memo(({ isOpen, onClose }) => {
  const { loadLoggedInUser } = useContext(GlobalContext)
  const { switchToUnify } = useUnify()
  const [busy, setBusy] = useState(false)
  const [step, setStep] = useState(0)

  const slides = TOUR.slides
  const last = slides.length // the "Ready to switch?" step
  const slide = slides[step]

  useEffect(() => {
    if (!isOpen) return undefined
    setStep(0)
    document.body.classList.add(INTRO_OPEN_CLASS)
    return () => document.body.classList.remove(INTRO_OPEN_CLASS)
  }, [isOpen])

  const go = useCallback((to) => setStep(Math.max(0, Math.min(last, to))), [last])

  // Left and right arrow keys move between slides.
  useEffect(() => {
    if (!isOpen) return undefined
    const onKey = (event) => {
      if (event.key === 'ArrowRight') go(step + 1)
      if (event.key === 'ArrowLeft') go(step - 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, step, go])

  const markSeen = useCallback(async () => {
    try {
      await updateUnifyPreference({ introSeen: true })
    } catch (err) {
      // best effort; the introduction shows again next sign-in
    }
    loadLoggedInUser()
  }, [loadLoggedInUser])

  const notNow = useCallback(async () => {
    setBusy(true)
    await markSeen()
    setBusy(false)
    onClose()
  }, [markSeen, onClose])

  const open = useCallback(async (optIn) => {
    setBusy(true)
    await markSeen()
    const ok = await switchToUnify({ optIn })
    if (!ok) {
      setBusy(false)
      onClose()
    }
  }, [markSeen, switchToUnify, onClose])

  return (
    <Modal isOpen={isOpen} size='lg' centered backdrop='static' contentClassName='unify-promo unify-intro unify-tour' modalClassName='unify-intro-wrap'>
      <section className='unify-intro__hero unify-tour__hero'>
        <div className='unify-intro__glow' aria-hidden='true' />
        <img className='unify-intro__wordmark' src={`${process.env.PUBLIC_URL || ''}/img/unify/wordmark-white.png`} alt='Sematics' />
        <span className='unify-label unify-label--on-navy'>{TOUR.label}</span>
        <div aria-live='polite' aria-atomic='true'>
          <h2 className='unify-intro__title'>{step < last ? slide.title : TOUR.final.title}</h2>
          <p className='unify-intro__lede'>{step < last ? slide.text : TOUR.final.text}</p>
        </div>
      </section>

      <section className='unify-tour__stage' aria-roledescription='carousel' aria-label="What's new in Unify">
        {step < last ? (
          <img key={slide.image} className='unify-tour__image' src={imageUrl(slide.image)} alt={slide.alt} />
        ) : (
          <div className='unify-tour__final'>
            <button type='button' className='unify-pill unify-pill--ink' disabled={busy} onClick={() => open(true)}>
              {TOUR.switchButton}
            </button>
            <button type='button' className='unify-pill unify-pill--line-ink' disabled={busy} onClick={() => open(false)}>
              {TOUR.lookButton}
            </button>
            <button type='button' className='unify-tour__text-button' disabled={busy} onClick={notNow}>
              {TOUR.laterButton}
            </button>
          </div>
        )}
      </section>

      <footer className='unify-tour__nav'>
        <button type='button' className='unify-tour__text-button' disabled={busy} onClick={step === 0 ? notNow : () => go(step - 1)}>
          {step === 0 ? TOUR.skip : TOUR.back}
        </button>
        <div className='unify-tour__dots' role='tablist' aria-label='Slides'>
          {[...slides, TOUR.final].map((item, i) => (
            <button
              key={item.title}
              type='button'
              role='tab'
              aria-selected={i === step}
              aria-label={`${i + 1} of ${last + 1}: ${item.title}`}
              className={`unify-tour__dot${i === step ? ' unify-tour__dot--active' : ''}`}
              onClick={() => go(i)}
            />
          ))}
        </div>
        {step < last ? (
          <button type='button' className='unify-pill unify-pill--ink unify-tour__next' onClick={() => go(step + 1)}>
            {TOUR.next}
          </button>
        ) : (
          <span className='unify-tour__next-spacer' aria-hidden='true' />
        )}
      </footer>
    </Modal>
  )
})

export default UnifyIntroModal
